import { useQueryClient } from '@tanstack/react-query';
import { ImageUp, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiRequestError, removeLogo, saveBranding, uploadLogo, type Branding } from '../../api/client';
import { Card } from '../../ui/components';
import { BRANDING_KEY, useBranding } from '../auth/session';

type Variant = 'light' | 'dark' | 'mark';
const VARIANTS: {
  key: Variant;
  label: 'branding.logoLight' | 'branding.logoDark' | 'branding.logoMark';
  bg: string;
}[] = [
  { key: 'light', label: 'branding.logoLight', bg: '#ffffff' },
  { key: 'dark', label: 'branding.logoDark', bg: '#0d1117' },
  { key: 'mark', label: 'branding.logoMark', bg: 'var(--color-surface-2)' },
];

/** Admin-only (settings.manage): logos and marketing texts shown on the sign-in page and in the app. */
export function BrandingCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const branding = useBranding();
  const [draft, setDraft] = useState<Pick<Branding, 'product_name' | 'marketing'> | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const inputs = useRef<Partial<Record<Variant, HTMLInputElement | null>>>({});

  useEffect(() => {
    if (branding.data && !draft)
      setDraft({ product_name: branding.data.product_name, marketing: branding.data.marketing });
  }, [branding.data, draft]);

  const refresh = () => qc.invalidateQueries({ queryKey: BRANDING_KEY }); // targeted: branding only
  const fail = (e: unknown) => setStatus(`error:${e instanceof ApiRequestError ? e.code : 'NETWORK'}`);

  const onFile = async (v: Variant, f: File | undefined) => {
    if (!f) return;
    try {
      await uploadLogo(v, f);
      setStatus('saved');
      await refresh();
    } catch (e) {
      fail(e);
    }
  };
  const setText = (lang: 'en' | 'ro', field: 'title' | 'subtitle', value: string) =>
    setDraft(
      (d) =>
        d && {
          ...d,
          marketing: {
            ...d.marketing,
            [lang]: { title: '', subtitle: '', ...d.marketing[lang], [field]: value },
          },
        },
    );

  if (!draft) return null;
  return (
    <Card title={t('branding.title')}>
      <p className="muted" style={{ marginTop: 0 }}>
        {t('branding.hint')}
      </p>
      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
        {VARIANTS.map((v) => {
          const url = branding.data?.logos[v.key];
          return (
            <div key={v.key} className="logo-slot">
              <span className="field">{t(v.label)}</span>
              <div className="logo-preview" style={{ background: v.bg }}>
                {url ? <img src={url} alt="" /> : <span className="muted">—</span>}
              </div>
              <div className="toolbar">
                <input
                  ref={(el) => void (inputs.current[v.key] = el)}
                  type="file"
                  hidden
                  accept="image/svg+xml,image/png,image/jpeg,image/webp"
                  onChange={(e) => void onFile(v.key, e.target.files?.[0])}
                />
                <button type="button" className="btn" onClick={() => inputs.current[v.key]?.click()}>
                  <ImageUp size={15} /> {t('branding.upload')}
                </button>
                {url && (
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={() => void removeLogo(v.key).then(refresh, fail)}
                    aria-label={t('branding.remove')}
                  >
                    <Trash2 size={15} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
      <p className="muted" style={{ fontSize: 'var(--fs-xs)' }}>
        {t('branding.formats')}
      </p>
      <div className="grid" style={{ gap: 'var(--space-3)', marginTop: 'var(--space-4)' }}>
        <label className="field">
          <span>{t('branding.productName')}</span>
          <input
            className="input"
            value={draft.product_name}
            maxLength={80}
            onChange={(e) => setDraft({ ...draft, product_name: e.target.value })}
          />
        </label>
        {(['en', 'ro'] as const).map((lang) => (
          <fieldset key={lang} className="fieldset">
            <legend>{lang === 'en' ? 'English' : 'Română'}</legend>
            <label className="field">
              <span>{t('branding.marketingTitle')}</span>
              <input
                className="input"
                maxLength={120}
                value={draft.marketing[lang]?.title ?? ''}
                onChange={(e) => setText(lang, 'title', e.target.value)}
              />
            </label>
            <label className="field">
              <span>{t('branding.marketingSubtitle')}</span>
              <textarea
                className="input textarea"
                maxLength={300}
                rows={2}
                value={draft.marketing[lang]?.subtitle ?? ''}
                onChange={(e) => setText(lang, 'subtitle', e.target.value)}
              />
            </label>
          </fieldset>
        ))}
        <div className="toolbar">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void saveBranding(draft).then(() => (setStatus('saved'), refresh()), fail)}
          >
            {t('branding.save')}
          </button>
          {status === 'saved' && <span className="status status-online">{t('branding.saved')}</span>}
          {status?.startsWith('error:') && (
            <span className="form-error">
              {t(`branding.errors.${status.slice(6)}` as 'branding.errors.FORBIDDEN', {
                defaultValue: t('login.errors.GENERIC'),
              })}
            </span>
          )}
        </div>
      </div>
    </Card>
  );
}
